/**
 * Locale Store
 *
 * Manages translation bundle loading lifecycle. Works in concert with the
 * i18n plugin.
 *
 * Responsibilities:
 * - Track loading state
 * - Coordinate post-auth bundle reload with tenant context
 * - Provide a Pinia-based reactive interface for locale state
 *
 * The app uses ONLY translations returned by the locale API:
 *   GET /api/v1/locale/:domain (PUBLIC, domain = window.location.hostname)
 *
 * If the API fails or returns empty, the static 'en' bundle from
 * @tms-phoenix/tms-types is used as fallback.
 *
 * There is NO auto-switch logic — the language is always 'en' and the
 * actual translated values come entirely from the /:domain API response.
 */
import { defineStore } from 'pinia';
import i18next from 'i18next';
import {
  reloadTranslationsPostAuth,
  clearBundleCache,
  DEFAULT_LANGUAGE,
} from '@/core/plugins/i18n';

interface LocaleState {
  /** Currently active language code — always 'en'. Translations come from /:domain API. */
  currentLanguage: string;

  /** Whether a translation bundle is currently being loaded. */
  isLoading: boolean;

  /** Error message if bundle loading failed. */
  error: string | null;

  /** Whether the post-auth (full) bundle has been loaded. */
  isFullBundleLoaded: boolean;
}

export const useLocaleStore = defineStore('locale', {
  state: (): LocaleState => ({
    currentLanguage: DEFAULT_LANGUAGE,
    isLoading: false,
    error: null,
    isFullBundleLoaded: false,
  }),

  getters: {
    /** Current language display name. */
    currentLanguageName(): string {
      return 'English';
    },

    /** Whether the current language is RTL. */
    isRtl(): boolean {
      return false;
    },
  },

  actions: {
    /**
     * Reload translations after authentication.
     *
     * Call this from the auth store's login action after successful auth.
     * Forces a bundle re-fetch from GET /api/v1/locale/:domain so the
     * user sees the latest translations including any tenant overrides.
     *
     * Flow:
     *   1. reloadTranslationsPostAuth() → i18next re-fetches from:
     *      GET /api/v1/locale/:domain (PUBLIC)
     *   2. The bundle includes resolved values with tenant custom overrides
     */
    async loadFullBundle(): Promise<void> {
      if (this.isFullBundleLoaded) return;

      this.isLoading = true;
      this.error = null;

      try {
        await reloadTranslationsPostAuth();
        this.isFullBundleLoaded = true;
      } catch (err) {
        this.error = err instanceof Error ? err.message : i18next.t('locale.loadBundleFailed');
        console.error('[LocaleStore] Full bundle load failed:', err);
        // Non-fatal: static 'en' fallback from @tms-phoenix/tms-types still works
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Reload the bundle after an admin terminology change.
     *
     * Called from Settings > Localization after a PUT /override or
     * DELETE /override succeeds. Forces i18next to re-fetch the bundle
     * from the API so the admin sees the updated terms immediately.
     *
     * Flow:
     *   1. Admin saves override via PUT /api/v1/locale/override (Override API)
     *   2. Server updates DB + S3 + invalidates CloudFront
     *   3. This method forces i18next.reloadResources()
     *   4. All reactive $t() bindings update automatically
     */
    async reloadAfterOverrideChange(): Promise<void> {
      this.isLoading = true;
      try {
        await reloadTranslationsPostAuth();
      } catch (err) {
        console.error('[LocaleStore] Bundle reload after override change failed:', err);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Reset state on logout — clear bundle cache and mark full bundle as
     * unloaded so it reloads on next login with potentially different
     * tenant/hospital context.
     */
    resetOnLogout(): void {
      clearBundleCache();
      this.isFullBundleLoaded = false;
      this.error = null;
    },
  },
});
